import { GoogleAuthProvider, signInWithCustomToken, signInWithPopup } from 'firebase/auth'
import { Bolt, Shield, Store } from 'lucide-react'
import { useEffect, useState } from 'react'
import { FcGoogle } from 'react-icons/fc'
import { Link, useNavigate } from 'react-router-dom'
import { auth } from '../config/firebase'
import { useAnalytics } from '../hooks/useAnalytics'
import { AuthService } from '../services/authService'
import useToastStore from '../store/useToastStore'
import { AnalyticsEvents } from '../utils/analytics'
import { setMetaUser, trackMetaStandard } from '../config/metaPixel'
import { AppInstallGuidelines } from '../components/admin/AppInstallGuidelines'
import { usePWAInstall } from '../hooks/usePWAInstall'

export const LoginPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [isInstagramWebView, setIsInstagramWebView] = useState(false);
  const toast = useToastStore();

  const { track, identify } = useAnalytics()
  const { isInstalled, installApp } = usePWAInstall();
  const [deviceOs, setDeviceOs] = useState<'ios' | 'android' | 'desktop' | 'unknown'>('unknown');

  // Detect Instagram WebView
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isInstagram = userAgent.includes('instagram');
    const isInAppBrowser = userAgent.includes('instagram') ||
      userAgent.includes('fbav') ||
      userAgent.includes('fban') ||
      userAgent.includes('fbauth');

    setIsInstagramWebView(isInstagram || isInAppBrowser);
  }, []);

  useEffect(() => {
    // Detect OS
    const ua = navigator.userAgent.toLowerCase();
    if (/android/i.test(ua)) {
      setDeviceOs('android');
    } else if (/iphone|ipad|ipod/i.test(ua)) {
      setDeviceOs('ios');
    } else if (/mac|windows|linux/i.test(ua)) {
      setDeviceOs('desktop');
    }
  }, []);

  const handleGoogleLogin = async () => {
    try {
      setIsLoading(true);

      // Sign in with Google Firebase
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);

      // Get user information for analytics
      const user = result.user;

      console.log(user);

      // Get the ID token
      const idToken = await user.getIdToken();

      // Call backend API
      const response = await AuthService.googleLogin({ token: idToken });

      if (!response.entityExists) {
        identify({
          id: user.uid,
          email: user.email || '',
          name: user.displayName || '',
          properties: {
            $email: user.email,
            $name: user.displayName,
            login_method: 'google',
            email_verified: user.emailVerified,
            photo_url: user.photoURL,
            signup_date: new Date().toISOString(),
            firebase_uid: user.uid,
          },
        });
        track({
          name: AnalyticsEvents.USER_SIGNUP,
          properties: { signup_method: 'google' },
        });

        // Meta pixel — merchant acquisition funnel: signup complete (Google path).
        // Mirrors the mobile-OTP branch in handleVerifyOtp: fire only for a NEW
        // entity, on a successful googleLogin response. eventID matches the backend
        // CAPI id (signup_${entityId}) so the browser and server copies deduplicate.
        setMetaUser({ email: user.email || response.email || undefined, externalId: response.entityId });
        trackMetaStandard('CompleteRegistration', `signup_${response.entityId}`, {
          content_name: 'merchant_signup',
        });
      }

      track({
        name: AnalyticsEvents.USER_LOGIN,
        properties: { login_method: 'google' },
      });

      console.log(response);

      // Refresh Firebase token to get updated claims
      await AuthService.reFreshToken();

      // Redirect to home page (which will handle routing to dashboard if authenticated)
      if (response.isStoreSetup) {
        navigate('/dashboard');
      } else {
        navigate('/onboarding');
      }

    } catch (error) {
      console.error('Login error:', error);

      // Track login error
      track({
        name: AnalyticsEvents.ERROR_OCCURRED,
        properties: {
          error_type: 'login_failed',
          error_message: error instanceof Error ? error.message : 'Unknown error',
          login_method: 'google',
        },
      });

      toast.showToast('Failed to sign in. Please try again.', 'error');
      // If there was an error, sign out from Firebase to maintain consistent state
      await AuthService.logout();
    } finally {
      setIsLoading(false);
    }
  }

  const handleSendOtp = async () => {
    if (!mobile.trim() || mobile.length !== 10) {
      toast.showToast('Please enter a valid 10-digit mobile number', 'error');
      return;
    }

    try {
      setIsSendingOtp(true);
      await AuthService.sendOtp({ mobile: mobile.trim() });
      setOtpSent(true);
      toast.showToast('OTP sent successfully to your mobile number', 'success');

      track({
        name: AnalyticsEvents.OTP_REQUESTED,
        properties: { mobile_last4: mobile.slice(-4) },
      });
    } catch (error) {
      console.error('Send OTP error:', error);
      toast.showToast('Failed to send OTP. Please try again.', 'error');
    } finally {
      setIsSendingOtp(false);
    }
  }

  const handleVerifyOtp = async () => {
    if (!otp.trim() || otp.length !== 6) {
      toast.showToast('Please enter a valid 6-digit OTP', 'error');
      return;
    }

    try {
      setIsVerifyingOtp(true);
      const response = await AuthService.verifyOtp({ mobile: mobile.trim(), otp: otp.trim() });

      // Sign in to Firebase with custom token
      if (response.customToken) {
        await signInWithCustomToken(auth, response.customToken);

        const firebaseUid = auth.currentUser?.uid || response.entityId?.toString() || 'unknown';

        if (!response.entityExists) {
          identify({
            id: firebaseUid,
            email: response.email || '',
            name: response.name || '',
            properties: {
              $email: response.email || undefined,
              $name: response.name || undefined,
              $phone: mobile,
              signup_method: 'mobile',
              entity_id: response.entityId,
              firebase_uid: firebaseUid,
              signup_date: new Date().toISOString(),
            },
          });
          track({
            name: AnalyticsEvents.USER_SIGNUP,
            properties: { signup_method: 'mobile' },
          });

          // Meta pixel — merchant acquisition funnel: signup complete.
          // eventID matches the backend CAPI id (signup_${entityId}) so the
          // browser and server copies deduplicate.
          setMetaUser({ phone: mobile, email: response.email || undefined, externalId: response.entityId });
          trackMetaStandard('CompleteRegistration', `signup_${response.entityId}`, {
            content_name: 'merchant_signup',
          });
        } else {
          // Returning mobile user — update phone on profile
          identify({
            id: firebaseUid,
            properties: {
              $phone: mobile,
              firebase_uid: firebaseUid,
            },
          });

          // Attach advanced-matching identifiers for later funnel events.
          setMetaUser({ phone: mobile, email: response.email || undefined, externalId: response.entityId });
        }

        track({
          name: AnalyticsEvents.OTP_VERIFIED,
          properties: { login_method: 'mobile' },
        });
        track({
          name: AnalyticsEvents.USER_LOGIN,
          properties: { login_method: 'mobile' },
        });

        // Refresh Firebase token to get updated claims
        await AuthService.reFreshToken();

        // Redirect based on response
        if (response.isStoreSetup) {
          navigate('/dashboard');
        } else {
          navigate('/onboarding');
        }
      } else {
        throw new Error('No custom token received from server');
      }
    } catch (error) {
      console.error('Verify OTP error:', error);
      toast.showToast('Invalid OTP. Please try again.', 'error');
    } finally {
      setIsVerifyingOtp(false);
    }
  }

  const resetMobileLogin = () => {
    setMobile('');
    setOtp('');
    setOtpSent(false);
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Section - Login */}
      <div className="w-full md:w-[45%] min-h-screen bg-white flex items-center justify-center px-4 md:px-8 lg:px-12 py-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl flex items-center justify-center">
              <Store className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              Mart-24
            </span>
          </Link>

          <div className="mb-6">

            <h2 className="text-xl md:text-3xl font-bold text-gray-900 mb-2">
              Sign in to your account
            </h2>
            <p className="text-gray-600 text-sm md:text-base">
              Access your dashboard and manage your store
            </p>
          </div>

          {/* Google Login - Only show if not in Instagram WebView */}
          {!isInstagramWebView && (
            <>
              <button
                onClick={handleGoogleLogin}
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-3 bg-white text-gray-900 py-3 px-4 rounded-xl border border-gray-200 hover:border-purple-200 hover:shadow-lg transition-all duration-300 transform hover:scale-105 mb-6 ${isLoading ? 'opacity-70 cursor-not-allowed' : ''
                  }`}
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-gray-900 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <FcGoogle className="text-xl" />
                )}
                <span className="font-medium">
                  {isLoading ? 'Signing in...' : 'Continue with Google'}
                </span>
              </button>

              {/* Divider */}
              <div className="flex items-center mb-6">
                <div className="flex-1 border-t border-gray-300"></div>
                <span className="px-4 text-sm text-gray-500">or</span>
                <div className="flex-1 border-t border-gray-300"></div>
              </div>
            </>
          )}

          {/* Mobile Login */}
          {!otpSent ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number
              </label>
              <div className="flex gap-3">
                <input
                  type="tel"
                  value={mobile}
                  onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="Enter 10-digit mobile number"
                  className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  maxLength={10}
                />
                <button
                  onClick={handleSendOtp}
                  disabled={isSendingOtp || mobile.length !== 10}
                  className={`px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors ${isSendingOtp || mobile.length !== 10 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                  {isSendingOtp ? 'Sending...' : 'Send OTP'}
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center mb-4">
                <button
                  onClick={resetMobileLogin}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium underline"
                >
                  Edit Mobile Number
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enter OTP sent to {mobile}
                </label>
                <div className="flex gap-3">
                  <input
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6-digit OTP"
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    maxLength={6}
                  />
                  <button
                    onClick={handleVerifyOtp}
                    disabled={isVerifyingOtp || otp.length !== 6}
                    className={`px-4 py-3 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors ${isVerifyingOtp || otp.length !== 6 ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                  >
                    {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </button>
                </div>
              </div>

              <div className="text-center">
                <button
                  onClick={handleSendOtp}
                  disabled={isSendingOtp}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                >
                  {isSendingOtp ? 'Sending...' : 'Resend OTP'}
                </button>
              </div>
            </div>
          )}

          {/* App Install Guidelines */}
          {!isInstalled && deviceOs !== 'unknown' && (
            <AppInstallGuidelines os={deviceOs} onInstall={installApp} />
          )}

        </div>
      </div>

      {/* Right Section - Hero Content */}
      <div className="hidden md:flex md:w-[55%] bg-gradient-to-br from-purple-50 via-white to-blue-50 items-center justify-center p-8 lg:p-12 relative overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-blue-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-1000"></div>
          <div className="absolute bottom-20 left-20 w-40 h-40 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        </div>

        <div className="max-w-2xl relative z-10">
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-purple-100 p-8 shadow-xl">
            <h2 className="text-3xl lg:text-4xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 bg-clip-text text-transparent mb-4 animate-gradient">
              Create Your E-commerce Store Instantly
            </h2>
            <p className="text-gray-600 text-lg mb-6">
              Launch your online store instantly with our powerful platform. Connect with your customers,
              manage products effortlessly, and grow your business with advanced analytics.
            </p>

            {/* Feature List */}
            <div className="space-y-4">
              {[
                { icon: Store, title: "Easy to use dashboard", color: "purple" },
                { icon: Shield, title: "Advanced analytics", color: "blue" },
                { icon: Bolt, title: "24/7 Customer support", color: "purple" }
              ].map((feature, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full bg-${feature.color}-100 flex items-center justify-center`}>
                    <feature.icon className={`w-4 h-4 text-${feature.color}-600`} />
                  </div>
                  <p className="text-gray-700">{feature.title}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 3s ease infinite;
        }
        .animation-delay-1000 { animation-delay: 1s; }
        .animation-delay-2000 { animation-delay: 2s; }
      `}</style>
    </div>
  )
} 